import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function SecurityTab() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      api.put('/account/password', data),
    onSuccess: () => {
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Passwort erfolgreich geändert');
    },
    onError: (err) => {
      toast.error((isAxiosError(err) ? err.response?.data?.detail : undefined) || 'Fehler beim Ändern des Passworts');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setValidationError('Neue Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 8) {
      setValidationError('Neues Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setValidationError('Neues Passwort muss mindestens einen Großbuchstaben enthalten');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setValidationError('Neues Passwort muss mindestens einen Kleinbuchstaben enthalten');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setValidationError('Neues Passwort muss mindestens eine Ziffer enthalten');
      return;
    }

    mutation.mutate({ current_password: currentPassword, new_password: newPassword });
  };

  const inputClass =
    'block w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors';

  return (
    <Card variant="glass">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Passwort ändern
        </CardTitle>
        <CardDescription>
          Legen Sie ein neues Passwort fest. Mindestens 8 Zeichen, Groß-/Kleinbuchstaben und Ziffer.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5 max-w-md">
          {validationError && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-800 dark:text-green-300">
                Passwort wurde erfolgreich geändert.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
            <input
              id="currentPassword"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputClass}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="newPassword">Neues Passwort</Label>
            <input
              id="newPassword"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputClass}
              placeholder="Mindestens 8 Zeichen"
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Neues Passwort bestätigen</Label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass}
              placeholder="Passwort wiederholen"
              autoComplete="new-password"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Wird geändert…' : 'Passwort ändern'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
